import { useEffect, useRef, useState } from 'react';

// Loading states (M66, docs/loading-states.md). One rule for every view that
// waits on the server, so nothing flashes and nothing is left blank:
//
// - D248: the mark appears only if the wait passes SHOW_AFTER_MS, and then
//   stays at least HOLD_FOR_MS. Cached data (an atom, IndexedDB, a persisted
//   atom) shows at once: pass `hasData` and nothing is shown over it.
// - D249: after SLOW_AFTER_MS a quiet "still loading" line; after a failure
//   (or when offline) a Try again.
export const SHOW_AFTER_MS = 150;
export const HOLD_FOR_MS = 300;
export const SLOW_AFTER_MS = 8000;

export interface LoadingStateInput {
  /** The request is in flight. */
  loading: boolean;
  /** Something is already on screen for this view (cache, an earlier page). */
  hasData?: boolean;
  /** The last load failed (an error, a message, or just `true`). */
  error?: unknown;
  /** Load again; wired to Try again and to coming back online. */
  retry?: () => void;
}

export interface LoadingState {
  /** Render the loading area instead of the content. */
  show: boolean;
  /** The mark is visible (after 150 ms; held 300 ms). */
  loader: boolean;
  /** The wait has passed 8 s. */
  slow: boolean;
  /** The load failed and there is nothing to show. */
  failed: boolean;
  /** The device reports no connection. */
  offline: boolean;
  retry?: () => void;
}

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

export function useLoadingState({
  loading,
  hasData = false,
  error,
  retry,
}: LoadingStateInput): LoadingState {
  // Waiting with nothing to show: the only time anything is drawn (D248).
  const waiting = loading && !hasData;
  const [loader, setLoader] = useState(false);
  const [slow, setSlow] = useState(false);
  const [offline, setOffline] = useState(isOffline);
  // When the mark appeared, for the minimum hold.
  const shownAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (waiting) {
      const timers = [
        setTimeout(() => {
          shownAtRef.current = Date.now();
          setLoader(true);
        }, SHOW_AFTER_MS),
        setTimeout(() => setSlow(true), SLOW_AFTER_MS),
      ];
      return () => timers.forEach(clearTimeout);
    }
    // The wait is over. Never shown: nothing to undo (a fast load shows
    // nothing at all). Shown: keep it to the minimum, then let go.
    setSlow(false);
    const shownAt = shownAtRef.current;
    if (shownAt === null) {
      setLoader(false);
      return;
    }
    const remaining = shownAt + HOLD_FOR_MS - Date.now();
    const timer = setTimeout(
      () => {
        shownAtRef.current = null;
        setLoader(false);
      },
      Math.max(0, remaining)
    );
    return () => clearTimeout(timer);
  }, [waiting]);

  const failed = !loading && !hasData && Boolean(error);

  // Offline: say so instead of "failed", and load again when back (D249).
  const retryRef = useRef(retry);
  retryRef.current = retry;
  const failedRef = useRef(failed);
  failedRef.current = failed;
  useEffect(() => {
    const onOnline = () => {
      setOffline(false);
      if (failedRef.current) retryRef.current?.();
    };
    const onOffline = () => setOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  return {
    show: waiting || loader || failed,
    loader: loader && !failed,
    slow: slow && loader && !failed,
    failed,
    offline,
    retry,
  };
}
